import { useMemo, memo } from 'react';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import SourceDescriptionIcon from './SourceDescriptionIcon';
import { Text } from 'foundations-components/components/Text';
import SourceUserAndAvatar from './SourceUserAndAvatar';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledFormattedReactMessage = styled(FormattedReactMessage).withConfig({
  displayName: "SourceInterpretationSentence__StyledFormattedReactMessage",
  componentId: "sc-1b29rl6-0"
})(["display:flex;align-items:center;flex-wrap:wrap !important;"]);
const SourceInterpretationSentence = ({
  label,
  description,
  instance,
  user
}) => {
  // We want all components to be conditionally integrated into one inline sentence
  // to preserve line break behaviors, but providing null/nonexistent react elements
  // as I18n translation options will cause an error. So we pass empty spans instead.
  // Alternatively, we could create many different translations for every sentence where
  // some combination of components are missing, but that would put a large and potentially
  // confusing burden on our translators.
  const moreInfoIcon = useMemo(() => description ? /*#__PURE__*/_jsx(Flex, {
    marginBlockStart: "0",
    children: /*#__PURE__*/_jsx(SourceDescriptionIcon, {
      description: description
    })
  }) : /*#__PURE__*/_jsx(View, {
    as: "span"
  }), [description]);
  const linkToInstance = useMemo(() => {
    if (!instance) {
      // this element can be undefined because the noInstance translations won't expect this option
      return undefined;
    }
    return /*#__PURE__*/_jsx(Link, {
      truncate: true,
      "data-test-id": "source-interpretation-instance-link",
      href: `${window.location.origin}/${instance.relativeUri}`,
      external: true,
      children: /*#__PURE__*/_jsx(View, {
        as: "span",
        overflowWrap: "break-word",
        children: instance.name
      })
    });
  }, [instance]);
  const userAndAvatar = useMemo(() => {
    if (!user) {
      // this element can be undefined because the noUser translations won't expect this option
      return undefined;
    }
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      display: "inline-flex",
      children: /*#__PURE__*/_jsx(SourceUserAndAvatar, {
        user: user,
        isInline: true
      })
    });
  }, [user]);
  const translationMessage = useMemo(() => {
    if (!user && !instance) {
      return 'crm-source-interpretation-lib.sourceInterpretationSentence.noUserNoLink';
    }
    if (!user) {
      return 'crm-source-interpretation-lib.sourceInterpretationSentence.noUser';
    }
    if (!instance) {
      return 'crm-source-interpretation-lib.sourceInterpretationSentence.noLink';
    }
    return 'crm-source-interpretation-lib.sourceInterpretationSentence.complete';
  }, [user, instance]);
  return /*#__PURE__*/_jsx(Text, {
    as: "small",
    "data-test-id": "source-interpretation-sentence",
    children: /*#__PURE__*/_jsx(Text, {
      as: "span",
      whiteSpace: "pre",
      children: /*#__PURE__*/_jsx(StyledFormattedReactMessage, {
        message: translationMessage,
        options: {
          user: userAndAvatar,
          label,
          moreInfoIcon,
          linkToInstance
        }
      })
    })
  });
};
export default /*#__PURE__*/memo(SourceInterpretationSentence);